import { useEffect, useMemo, useRef, useState } from "react";
import { Room, RoomEvent, Track } from "livekit-client";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Swords, Loader2 } from "lucide-react";

// Top-3 gifter avatars for one battle window (data from GET /battles/{id}/top-gifters).
// Tap an avatar to see who it is and how much they gifted in this battle.
function TopGifters({ list, side }) {
  const [open, setOpen] = useState(null);
  if (!list || !list.length) return null;
  const sel = list.find((g) => g.rank === open);
  return (
    <div className={`absolute top-10 ${side === "left" ? "left-1 items-start" : "right-1 items-end"} z-10 flex flex-col gap-1 pointer-events-auto`} data-testid={`battle-top3-${side}`}>
      <div className="flex items-center gap-1">
        {list.slice(0, 3).map((g) => (
          <button key={g.rank} type="button" onClick={() => setOpen(open === g.rank ? null : g.rank)}
            className="relative w-8 h-8 rounded-full border-2 border-[#FF7A00] bg-black/70 overflow-visible flex items-center justify-center text-[10px] font-bold"
            data-testid={`battle-top3-${side}-${g.rank}`}>
            {g.user?.avatar ? <img src={g.user.avatar} alt="" className="w-full h-full rounded-full object-cover"/> : (g.user?.username || "?")[0]?.toUpperCase()}
            <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#FF7A00] text-black text-[8px] font-extrabold flex items-center justify-center">{g.rank}</span>
          </button>
        ))}
      </div>
      {sel && (
        <div className="px-2 py-1 rounded-lg bg-black/80 border border-[#FF7A00]/60 text-[10px] font-bold whitespace-nowrap" data-testid={`battle-top3-${side}-detail`}>
          #{sel.rank} @{sel.user?.username || "user"} · <span className="text-[#FF7A00]">{sel.total_amount}</span> gifted
        </div>
      )}
    </div>
  );
}

/**
 * OpponentLiveWindow — second isolated LiveKit viewer connection.
 * Primary stream connection is never touched, so battle start/end
 * doesn't black out the main video.
 */
function OpponentLiveWindow({ streamId, label }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const roomRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!streamId) return;
    let cancelled = false;
    const setup = async () => {
      let data;
      try {
        const r = await api.post(`/live/${streamId}/join`);
        data = r.data || {};
      } catch { if (!cancelled) setFailed(true); return; }
      if (!data.livekit_token || !data.livekit_url) {
        if (!cancelled) setFailed(true); return;
      }
      const room = new Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = room;
      const attach = async (track) => {
        for (let i = 0; i < 20; i++) {
          const el = track.kind === Track.Kind.Video ? videoRef.current : audioRef.current;
          if (el) { try { track.attach(el); } catch { /* noop */ } if (track.kind === Track.Kind.Video) setReady(true); return; }
          await new Promise((r) => setTimeout(r, 50));
        }
      };
      room.on(RoomEvent.TrackSubscribed, (t) => { attach(t); });
      room.on(RoomEvent.TrackUnsubscribed, (t) => { try { t.detach(); } catch { /* noop */ } });
      try { await room.connect(data.livekit_url, data.livekit_token); }
      catch { if (!cancelled) setFailed(true); return; }
      if (cancelled) return;
      for (const p of room.remoteParticipants.values()) {
        for (const pub of p.trackPublications.values()) {
          if (pub.track) attach(pub.track);
          else if (!pub.isSubscribed) { try { pub.setSubscribed(true); } catch { /* noop */ } }
        }
      }
    };
    setup();
    return () => {
      cancelled = true;
      if (roomRef.current) { try { roomRef.current.disconnect(); } catch { /* noop */ } roomRef.current = null; }
      try { api.post(`/live/${streamId}/leave`).catch(() => {}); } catch { /* noop */ }
    };
  }, [streamId]);

  return (
    <>
      <video ref={videoRef} autoPlay playsInline className="absolute inset-0 w-full h-full object-cover"/>
      <audio ref={audioRef} autoPlay/>
      {!ready && !failed && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white/75">
          <Loader2 className="w-5 h-5 animate-spin mb-1"/>
          <span className="text-[10px] font-bold">Loading {label || "opponent"}…</span>
        </div>
      )}
      {failed && (
        <div className="absolute inset-0 flex items-center justify-center text-center px-2">
          <span className="text-[11px] text-white/80 font-bold">Could not connect</span>
        </div>
      )}
    </>
  );
}

/**
 * Window header — profile picture + @username only. Nothing else.
 * Overlays the top of each pane.
 */
function WindowHeader({ user, userId, side, linkable, role }) {
  if (!user) return null;
  const cls = `absolute top-1 ${side === "left" ? "left-1" : "right-1"} z-10 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60 backdrop-blur-md max-w-[90%]`;
  const body = (
    <>
      {role && (
        <span className={`shrink-0 px-1.5 py-[1px] rounded text-[9px] font-extrabold uppercase tracking-wide ${side === "left" ? "bg-[#00E5FF] text-black" : "bg-[#FF2A5F] text-white"}`} data-testid={`battle-role-${side}`}>{role}</span>
      )}
      {user.avatar && <img src={user.avatar} alt="" className="w-5 h-5 rounded-full border border-white/25"/>}
      <span className="text-[11px] font-extrabold truncate">@{user.username || "user"}</span>
    </>
  );
  // Viewers tap the @username to open that streamer's profile (support #273638).
  // Not linked on the streamer's own screen: navigating away would end their live.
  const id = user.id || userId;
  if (linkable && id) {
    return <Link to={`/u/${id}`} className={`${cls} pointer-events-auto active:scale-95`} data-testid={`battle-head-${side}`}>{body}</Link>;
  }
  return <div className={cls} data-testid={`battle-head-${side}`}>{body}</div>;
}

/**
 * BattleScoreBar — ONLY the countdown timer + the two scores. No "PK Battle vs"
 * text (support fix #3). Thin ribbon at the top of the screen.
 */
function BattleScoreBar({ battle, now, mySide, viewer }) {
  if (!battle) return null;
  const endsAt = battle.ends_at ? new Date(battle.ends_at).getTime() : 0;
  const msLeft = Math.max(0, endsAt - now);
  const s = Math.floor(msLeft / 1000);
  const timeStr = `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  // Scores follow the screen: left number = left window's streamer.
  const hostScore = (mySide === "host" ? battle.host_score : battle.opponent_score) ?? 0;
  const oppScore = (mySide === "host" ? battle.opponent_score : battle.host_score) ?? 0;
  const total = Math.max(1, hostScore + oppScore);
  const hostPct = Math.round((hostScore / total) * 100);
  return (
    <div className={`absolute top-[6.25rem] ${viewer ? "inset-x-2" : "inset-x-14"} z-30 rounded-full bg-black/70 backdrop-blur-md border border-[#FF7A00]/50 px-3 py-1 flex items-center gap-2`} data-testid="battle-score-bar">
      <span className="text-[11px] font-extrabold text-[#00E5FF] tabular-nums w-7 text-right" data-testid="battle-host-score">{hostScore}</span>
      <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-white/10 flex">
        <div className="h-full bg-gradient-to-r from-[#00E5FF] to-[#A855F7]" style={{ width: `${hostPct}%` }}/>
        <div className="h-full flex-1 bg-gradient-to-r from-[#FF2A5F] to-[#FF7A00]"/>
      </div>
      <span className="text-[11px] font-extrabold text-[#FF2A5F] tabular-nums w-7" data-testid="battle-opp-score">{oppScore}</span>
      <span className="absolute left-1/2 -translate-x-1/2 -bottom-5 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/75 border border-[#FF7A00]/50 text-[#FF7A00] font-extrabold text-xs tabular-nums" data-testid="battle-timer">
        <Swords className="w-3 h-3"/>{timeStr}
      </span>
    </div>
  );
}

/**
 * BattleSplitView — renders a strict 50/50 split: the RIGHT pane contains the
 * opponent LiveKit video, the LEFT pane is a transparent header overlay for
 * the primary (which the caller shrinks to w-1/2 left-0 via CSS). Both panes
 * use `top-24 bottom-32` to guarantee identical heights — matched by the
 * caller's primary video CSS.
 */
export default function BattleSplitView({ battle, mySide, viewer = false }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const [top, setTop] = useState({});
  const battleId = battle?.id;
  const battleActive = battle?.status === "active";
  useEffect(() => {
    if (!battleId || !battleActive) { setTop({}); return undefined; }
    let alive = true;
    const load = () => api.get(`/battles/${battleId}/top-gifters`).then((r) => { if (alive) setTop(r.data || {}); }).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [battleId, battleActive]);
  const otherStreamId = useMemo(() => {
    if (!battle) return null;
    return mySide === "host" ? battle.opponent_stream_id : battle.host_stream_id;
  }, [battle, mySide]);
  if (!battle || battle.status !== "active") return null;

  const leftUser = mySide === "host" ? battle.host : battle.opponent;
  const rightUser = mySide === "host" ? battle.opponent : battle.host;
  const leftUserId = mySide === "host" ? battle.host_user_id : battle.opponent_user_id;
  const rightUserId = mySide === "host" ? battle.opponent_user_id : battle.host_user_id;
  // Labels follow the screen, not the battle invite: whichever stream is being
  // watched is on the left and is the "Host" (support #273638).
  const leftLabel = "Host";
  const rightLabel = "Opponent";

  return (
    <>
      {/* Right pane — opponent video. Exact mirror of left-pane CSS so both
          panes are identical heights (no overlap, no gap). */}
      <div className="absolute battle-half right-0 w-1/2 z-20 overflow-hidden bg-black border-l border-white/10 pointer-events-auto" data-testid="battle-opponent-pane">
        {otherStreamId ? (
          <OpponentLiveWindow streamId={otherStreamId} label={`@${rightUser?.username || "opponent"}`}/>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/50 text-xs font-bold">Waiting…</div>
        )}
        <WindowHeader user={rightUser} userId={rightUserId} side="right" linkable={viewer} role={rightLabel}/>
        <TopGifters list={top[otherStreamId]} side="right"/>
      </div>
      {/* Left pane header overlay — primary video is positioned by the caller
          at the same top-24 bottom-32 left-0 w-1/2 rect. We overlay just the
          avatar/username header so each window shows ONLY that host's info. */}
      <div className="absolute battle-half left-0 w-1/2 z-20 pointer-events-none" data-testid="battle-self-pane">
        <WindowHeader user={leftUser} userId={leftUserId} side="left" linkable={viewer} role={leftLabel}/>
        <TopGifters list={top[mySide === "host" ? battle.host_stream_id : battle.opponent_stream_id]} side="left"/>
      </div>
      <BattleScoreBar battle={battle} now={now} mySide={mySide} viewer={viewer}/>
    </>
  );
}
